import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const categoryName = getCategoryLabel(article.category);

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
      >
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-2/5 h-56 lg:h-auto relative overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:w-3/5 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                {categoryName}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400">{article.readTime}</span>
            </div>
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 leading-snug">
              {article.title}
            </h3>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed line-clamp-2">
              {article.summary}
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm font-medium text-primary-600 group-hover:gap-3 transition-all duration-200">
              <span>阅读全文</span>
              <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-lg border border-background-200/70 p-4 hover:border-primary-200/60 transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
              {categoryName}
            </span>
            <h4 className="mt-2 text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {article.title}
            </h4>
            <div className="mt-2 flex items-center gap-2 text-xs text-foreground-400">
              <span>{article.date}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
    >
      <div className="h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
            {categoryName}
          </span>
          <span className="text-xs text-foreground-400">{article.readTime}</span>
        </div>
        <h3 className="text-base font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.summary}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-foreground-400">{article.date}</span>
          <span className="text-xs font-medium text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            阅读 →
          </span>
        </div>
      </div>
    </Link>
  );
}

function getCategoryLabel(slug: string): string {
  const m: Record<string, string> = {
    'shengli-yuanli': '生理原理',
    'jiqiao-fangfa': '技巧方法',
    'jiankang-zhishi': '健康知识',
    'changjian-wuqu': '常见误区',
  };
  return m[slug] || slug;
}